import { useEffect } from "react";
import { isAxiosError } from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { hasOnlyRole } from "@myapp/contract";
import { useAuthStore } from "../../features/auth";
import { PhaseActionBar } from "../../features/work-session";
import { TechnicianFeedbackCard, useTaskTechnicianFeedback } from "../../features/technician-feedback";
import { hasTechnicianFeedback } from "../../shared/lib/technicianFeedback";
import { Loader, PageLayout } from "../../shared/ui";
import { useIsMobile } from "../../shared/hooks/useIsMobile";
import { PATHS } from "../../app/routes/paths";
import { useWorkerTask } from "./model/useWorkerTask";
import { useTaskFlows } from "./model/useTaskFlows";
import { TaskActions } from "./ui/TaskActions";
import { TaskDetailFields } from "./ui/TaskDetailFields";
import { TaskFlowDialogs } from "./ui/TaskFlowDialogs";
import { TaskSecondaryActions } from "./ui/TaskSecondaryActions";

export const WorkerTaskPage = () => {
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId: string }>();
  const id = Number(taskId);

  const user = useAuthStore((state) => state.user);
  const isMobile = useIsMobile();
  const { isLoading, isError, data, error, taps, vm } = useWorkerTask(id);
  const flows = useTaskFlows({ taps });
  // Above the early returns, like every other hook here — takes `null` while loading.
  const feedback = useTaskTechnicianFeedback(vm?.task ?? null);

  // Above the early returns — hooks cannot be conditional.
  useEffect(() => {
    if (isLoading) return;

    if (isError) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 403 || status === 404) {
        navigate(PATHS.workerCalendar, { replace: true });
      }
      return;
    }

    if (data && hasOnlyRole(user?.roles ?? [], 'WORKER') && data.assignedTechnicianId !== Number(user?.id)) {
      navigate(PATHS.workerCalendar, { replace: true });
    }
  }, [data, error, isError, isLoading, navigate, user]);

  if (isLoading || (hasOnlyRole(user?.roles ?? [], 'WORKER') && data?.assignedTechnicianId !== Number(user?.id))) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  if (!vm) {
    return (
      <div className="flex items-center justify-center h-full text-red-400">
        Aufgabe konnte nicht geladen werden.
      </div>
    );
  }

  // Same decision, one bar: the desktop copy sits inline in PageLayout's header row, the mobile
  // copy pins to the bottom above the safe area. `TaskSecondaryActions` is the single place
  // "Umdisponieren" / "Im Kalender planen" is decided either way.
  const desktopActionBar = vm.showActionButtons ? (
    <>
      <TaskSecondaryActions
        appearance="header"
        canPlan={vm.canPlan}
        isTimeScheduled={vm.isTimeScheduled}
        onPlan={flows.onPlan}
      />
      {vm.primaryAction && (
        <PhaseActionBar
          action={vm.primaryAction}
          elapsedSince={vm.elapsedSince}
          isPending={taps.isPending}
          failed={taps.failed}
          confirmedAt={taps.confirmedAt}
          onAction={flows.onPrimaryAction}
          onUndo={taps.undo}
        />
      )}
    </>
  ) : undefined;

  const mobileActionBar = vm.showActionButtons ? (
    <TaskActions
      action={vm.primaryAction}
      canPlan={vm.canPlan}
      isTimeScheduled={vm.isTimeScheduled}
      elapsedSince={vm.elapsedSince}
      isPending={taps.isPending}
      failed={taps.failed}
      confirmedAt={taps.confirmedAt}
      onAction={flows.onPrimaryAction}
      onUndo={taps.undo}
      onPlan={flows.onPlan}
    />
  ) : undefined;

  return (
    <>
      <PageLayout
        pageTitle={vm.task.title}
        back
        headerChildren={isMobile ? undefined : desktopActionBar}
        bottomBar={isMobile ? mobileActionBar : undefined}
      >
        {/* `TaskActions` is `position: fixed` on mobile (a floating card, not a flex sibling), so
            nothing here reserves room for it automatically any more — this bottom padding is that
            reservation, restated locally rather than in shared `PageLayout` CSS. */}
        <div className={`space-y-3 ${isMobile && mobileActionBar ? "pb-32" : ""}`}>
          <TaskDetailFields
            task={vm.task}
            statusLabel={vm.statusLabel}
            statusColor={vm.statusColor}
            typeLabel={vm.typeLabel}
            phase={vm.phase}
            terminText={vm.terminText}
            terminUnscheduled={vm.terminUnscheduled}
            deadlineText={vm.deadlineText}
            deadlineOverdue={vm.deadlineOverdue}
            onDiscardTravel={taps.undo}
          />
          {/* Comment and files back to the office — same rule as the appointment page. */}
          {feedback && (vm.phase !== null || hasTechnicianFeedback(vm.task)) && (
            <TechnicianFeedbackCard controller={feedback} />
          )}
        </div>
      </PageLayout>
      <TaskFlowDialogs task={vm.task} technicianId={vm.technicianId} flows={flows} />
    </>
  );
};
